import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import adminApi from "../../services/adminApi";
import {
  FiUser, FiLock, FiSettings, FiBell, FiImage, FiCheckCircle,
  FiEye, FiEyeOff, FiCamera, FiMonitor,
  FiSave, FiChevronRight, FiChevronDown, FiSearch, FiX, FiUsers, FiShield, FiUserPlus, FiMusic,
  FiAlertTriangle,
} from "react-icons/fi";

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  olive:"#6B8A5A", olive2:"#8BA47A",
  orange:"#F29A0C", orange2:"#E97818",
  dark:"#1F1F1F", mid:"#555", muted:"#AAA", light:"#CCC",
  border:"rgba(239,229,216,0.8)",
  green:"#22C55E", greenDk:"#16A34A",
  red:"#E24B4A", amber:"#F59E0B", blue:"#185FA5",
};

const glass = {
  background:"rgba(255,255,255,0.82)",
  border:"1px solid rgba(255,255,255,0.95)",
  backdropFilter:"blur(32px)", WebkitBackdropFilter:"blur(32px)",
  boxShadow:"0 20px 60px rgba(0,0,0,0.06),inset 0 1px 0 #fff",
};

// ─── SIDEBAR NAV ITEMS ────────────────────────────────────────────────────────
// superAdminOnly entries are filtered out of the nav for everyone else. The
// endpoints behind them are SuperAdmin-only server-side too — hiding the item is
// for clarity, not for security.
const NAV_ITEMS = [
  { id:"profile",    label:"Profile",               icon:FiUser,         desc:"Personal info & avatar" },
  { id:"security",   label:"Security",              icon:FiLock,         desc:"Password & active sessions" },
  { id:"team",       label:"Team Access",           icon:FiUsers,        desc:"Admins & superadmins", superAdminOnly:true },
  // { id:"platform",   label:"Platform Config",       icon:FiSettings,     desc:"Site settings & timezone" },
  // { id:"notifprefs", label:"Notification Prefs",    icon:FiBell,         desc:"Alert preferences" },
  { id:"appearance", label:"Appearance",            icon:FiImage,        desc:"Theme & branding" },
];

// Roles a SuperAdmin can hand out. Mirrors RoleNames on the backend, which
// validates the value against the Roles table before creating the account.
const STAFF_ROLES = [
  { value:"SuperAdmin", label:"Super Admin", desc:"Full access, including staff account management." },
  { value:"Admin",      label:"Admin",       desc:"Manages members, content, packages and subscriptions." },
  { value:"Trainer",    label:"Trainer",     desc:"Conducts sessions and classes." },
];


const NOTIF_PREFS = [
  { id:"new_user",       label:"New user registration",    desc:"When a new user signs up",            default:true },
  { id:"payment",        label:"Payment received",         desc:"Successful subscription payment",      default:true },
  { id:"failed_payment", label:"Failed payment",           desc:"When a payment fails",                 default:true },
  { id:"sub_expiry",     label:"Subscription expiring",    desc:"7 days before expiry",                 default:true },
  { id:"cash_pending",   label:"Cash activation pending",  desc:"Manual activation needed",             default:true },
  { id:"session_full",   label:"Session fully booked",     desc:"Live session at max capacity",         default:false },
  { id:"health_flag",    label:"Health concern flagged",   desc:"User health issue reported",           default:true },
  { id:"new_content",    label:"Content uploaded",         desc:"New video added to library",           default:false },
  { id:"report_ready",   label:"Weekly report ready",      desc:"Auto-generated analytics report",      default:true },
];

const TIMEZONES = ["IST (UTC+5:30)","UAE (UTC+4)","JST (UTC+9)","CET (UTC+1)","EST (UTC-5)","PST (UTC-8)"];
const CURRENCIES = ["INR (₹)","USD ($)","EUR (€)","AED (د.إ)","JPY (¥)"];

// ─── SMALL COMPONENTS ─────────────────────────────────────────────────────────
const Toggle = ({ on, onChange }) => (
  <motion.button type="button" onClick={() => onChange(!on)}
    className="relative rounded-full border-none cursor-pointer flex-shrink-0"
    style={{
      width:42, height:24,
      background: on ? `linear-gradient(135deg,${T.olive},${T.olive2})` : "rgba(239,229,216,0.9)",
      boxShadow: on ? `0 3px 10px rgba(107,138,90,0.4)` : "none",
      transition:"all 0.25s",
    }}>
    <motion.div animate={{ left: on ? 20 : 2 }}
      transition={{ type:"spring", stiffness:400, damping:28 }}
      className="absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white"
      style={{ boxShadow:"0 2px 4px rgba(0,0,0,0.2)" }}/>
  </motion.button>
);

const SectionHeader = ({ title, desc }) => (
  <div className="mb-6">
    <h2 className="font-black text-[20px] text-[#1F1F1F]"
      style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>{title}</h2>
    <p className="text-[13px] mt-1" style={{ color:T.muted }}>{desc}</p>
  </div>
);

// ─── CUSTOMIZED DROPDOWN COMPONENT ──────────────────────────────────────────
const CustomSelect = ({ value, onChange, options, placeholder="Select...", label, hasError = false, searchable = false, onBlur, required = false }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedObj = options.find(o => (typeof o === "string" ? o : o.value) === value);
  const displayLabel = typeof selectedObj === "string" ? selectedObj : selectedObj?.label || selectedObj?.value || value;

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt => {
      const val = typeof opt === "string" ? opt : String(opt.value || "");
      const lbl = typeof opt === "string" ? opt : String(opt.label || opt.value || "");
      return val.toLowerCase().includes(term) || lbl.toLowerCase().includes(term);
    });
  }, [options, searchTerm]);

  return (
    <div className="relative w-full" ref={ref}>
      {label && (
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] font-black uppercase tracking-[0.9px] block"
            style={{ color: hasError ? T.red : T.muted }}>
            {label} {required && <span style={{ color: T.red }}>*</span>}
          </label>
        </div>
      )}
      <div
        onClick={() => {
          setOpen(!open);
          if (open && onBlur) onBlur();
        }}
        className="w-full rounded-[14px] px-4 py-2.5 text-[13px] font-semibold flex items-center justify-between cursor-pointer transition-all"
        style={{
          border: `1.5px solid ${hasError ? T.red : open ? T.olive : T.border}`,
          background: hasError ? "rgba(226,75,74,0.04)" : "rgba(255,255,255,0.9)",
          color: value ? T.dark : T.muted,
          boxShadow: hasError ? "0 0 0 3px rgba(226,75,74,0.12)" : open ? "0 4px 16px rgba(107,138,90,0.15)" : "none",
          fontFamily: "'Inter',sans-serif",
        }}
      >
        <span className="truncate font-medium">{value ? displayLabel : placeholder}</span>
        <FiChevronDown size={15} style={{ color: open ? T.olive : T.muted, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}/>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 z-[300] max-h-60 overflow-hidden flex flex-col rounded-[18px] p-1.5 shadow-2xl border"
            style={{ background: "#FFFDF8", borderColor: "rgba(239,229,216,0.95)", boxShadow: "0 18px 45px rgba(0,0,0,0.12)" }}
          >
            {/* Search Input inside Dropdown Popover if enabled */}
            {searchable && options.length > 3 && (
              <div className="p-1 mb-1 border-b border-zinc-100/90 sticky top-0 bg-[#FFFDF8] z-10">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-100/90 border border-zinc-200/70 focus-within:border-[#6B8A5A] transition-all">
                  <FiSearch size={13} className="text-zinc-400 shrink-0" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full text-[11.5px] bg-transparent outline-none text-zinc-800 placeholder:text-zinc-400 font-medium"
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSearchTerm(""); }}
                      className="text-zinc-400 hover:text-zinc-600 bg-transparent border-none cursor-pointer p-0 flex items-center justify-center"
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="overflow-y-auto flex-1 max-h-48" style={{ scrollbarWidth: "none" }}>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-3 text-center text-[11.5px] text-zinc-400 italic">
                  No matching options found
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const val = typeof opt === "string" ? opt : opt.value;
                  const lbl = typeof opt === "string" ? opt : opt.label;
                  const desc = typeof opt === "object" ? opt.desc : null;
                  const isSelected = val === value;
                  return (
                    <div
                      key={val}
                      onClick={() => {
                        onChange(val);
                        setOpen(false);
                        setSearchTerm("");
                      }}
                      className="px-3.5 py-2.5 text-[12.5px] rounded-[12px] cursor-pointer flex items-center justify-between transition-colors mb-0.5"
                      style={{
                        background: isSelected ? "rgba(107,138,90,0.12)" : "transparent",
                        color: isSelected ? T.olive : T.dark,
                        fontWeight: isSelected ? "700" : "500"
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{lbl}</p>
                        {desc && <p className="text-[10.5px] mt-0.5 text-zinc-400 font-normal truncate">{desc}</p>}
                      </div>
                      {isSelected && <FiCheckCircle size={14} style={{ color: T.olive }} className="shrink-0 ml-2" />}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Enhanced InputField with robust inline validation feedback
const InputField = ({
  label,
  type = "text",
  placeholder,
  defaultValue,
  hint,
  value,
  onChange,
  onBlur,
  disabled,
  required = false,
  error = null,
  hasError = false,
  autoComplete,
}) => {
  const [focused, setFocused] = useState(false);
  const controlled = value !== undefined;
  const isErr = hasError && !!error;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] font-black uppercase tracking-[0.9px] block"
          style={{ color: isErr ? T.red : T.muted }}>
          {label} {required && <span style={{ color: T.red }}>*</span>}
        </label>
      </div>
      <input
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        {...(controlled
          ? { value: value ?? "", onChange: (e) => onChange(e.target.value) }
          : { defaultValue })}
        className="w-full rounded-[14px] px-4 py-2.5 text-[13px] outline-none"
        style={{
          border: `1.5px solid ${isErr ? T.red : focused ? T.olive : T.border}`,
          background: isErr ? "rgba(226,75,74,0.04)" : "rgba(255,255,255,0.9)",
          color: T.dark,
          fontFamily: "'Inter',sans-serif",
          boxShadow: isErr ? "0 0 0 3px rgba(226,75,74,0.12)" : focused ? `0 0 0 4px rgba(107,138,90,0.1)` : "none",
          transition: "all 0.2s",
        }}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          if (onBlur) onBlur(e);
        }}
      />
      {isErr ? (
        <p className="text-[11px] font-bold flex items-center gap-1 mt-1.5" style={{ color: T.red }}>
          <FiAlertTriangle size={12} className="shrink-0" /> {error}
        </p>
      ) : hint ? (
        <p className="text-[11px] mt-1.5" style={{ color: T.muted }}>{hint}</p>
      ) : null}
    </div>
  );
};

const SaveBar = ({ onSave, saving, onDiscard, saveLabel = "Save Changes" }) => (
  <div className="flex items-center justify-end gap-3 pt-5 mt-2 border-t"
    style={{ borderColor:"rgba(239,229,216,0.5)" }}>
    <motion.button whileHover={{ y:-1 }} onClick={onDiscard}
      className="h-10 px-5 rounded-full text-[13px] font-semibold cursor-pointer"
      style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
        color:T.mid, fontFamily:"'Inter',sans-serif" }}>
      Discard
    </motion.button>
    <motion.button whileHover={{ y:-1 }} whileTap={{ scale:0.97 }} onClick={onSave}
      className="h-10 px-6 rounded-full text-white text-[13px] font-bold flex items-center gap-2 border-none cursor-pointer"
      style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
        boxShadow:`0 5px 16px rgba(107,138,90,0.38)`, fontFamily:"'Inter',sans-serif" }}>
      {saving ? (
        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
      ) : <FiSave size={14}/>}
      {saving ? "Saving…" : saveLabel}
    </motion.button>
  </div>
);

// ─── SECTION: PROFILE ─────────────────────────────────────────────────────────
const initialsOf = (name) =>
  (name || "").trim().split(/\s+/).slice(0, 2).map(w => w[0] || "").join("").toUpperCase() || "AD";

const ProfileSection = ({ showToast }) => {
  const [form,    setForm]    = useState(null);   // null until loaded
  const [avatar,  setAvatar]  = useState(null);   // data: URL when a new file is picked
  const [removePhoto, setRemovePhoto] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [errors,  setErrors]  = useState({});
  const [touched, setTouched] = useState({});
  const fileRef = useRef(null);

  const validateField = (field, value) => {
    let err = null;
    const v = (value || "").toString().trim();

    if (field === "fullName") {
      if (!v) err = "Full name is required";
      else if (v.length < 2) err = "Full name must be at least 2 characters";
      else if (v.length > 100) err = "Full name cannot exceed 100 characters";
      else if (!/^[a-zA-Z\s.'\-]+$/.test(v)) err = "Full name can only contain letters, spaces, dots, and hyphens";
      else if (/<script|javascript:/i.test(v)) err = "Invalid characters detected";
    }

    if (field === "email") {
      if (!v) err = "Email address is required";
      else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)) err = "Enter a valid email address (e.g. name@domain.com)";
      else if (v.length > 150) err = "Email cannot exceed 150 characters";
    }

    if (field === "userName" && v) {
      if (v.length < 3 || v.length > 30) err = "Username must be between 3 and 30 characters";
      else if (!/^[a-zA-Z0-9_.-]+$/.test(v)) err = "Username can only contain letters, numbers, underscores, and hyphens";
      else if (/<script|javascript:/i.test(v)) err = "Invalid characters detected";
    }

    if (field === "phone" && v) {
      if (!/^\+?[0-9\s\-()]{7,20}$/.test(v)) err = "Enter a valid phone number (7–20 digits/symbols)";
    }

    if (field === "bio" && v) {
      if (v.length > 500) err = "Bio cannot exceed 500 characters";
      else if (/<script|javascript:/i.test(v)) err = "Invalid characters detected";
    }

    return err;
  };

  const validateAll = (data) => {
    const newErrors = {};
    const fnErr = validateField("fullName", data.fullName);
    if (fnErr) newErrors.fullName = fnErr;

    const emErr = validateField("email", data.email);
    if (emErr) newErrors.email = emErr;

    const unErr = validateField("userName", data.userName);
    if (unErr) newErrors.userName = unErr;

    const phErr = validateField("phone", data.phone);
    if (phErr) newErrors.phone = phErr;

    const bioErr = validateField("bio", data.bio);
    if (bioErr) newErrors.bio = bioErr;

    return newErrors;
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null); setErrors({}); setTouched({});
    try {
      const { data: p } = await adminApi.getMyProfile();
      setForm({
        fullName: p.fullName || "",
        email:    p.email    || "",
        userName: p.userName || "",
        phone:    p.phone    || "",
        bio:      p.bio      || "",
        role:     p.role     || "",
        profileImageUrl: p.profileImageUrl || "",
      });
      setAvatar(null);
      setRemovePhoto(false);
    } catch (e) {
      setError(e.message || "Could not load profile.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => {
    setForm(f => {
      const updated = { ...f, [k]: v };
      if (touched[k]) {
        const err = validateField(k, v);
        setErrors(prev => ({ ...prev, [k]: err }));
      }
      return updated;
    });
  };

  const blur = (k) => () => {
    setTouched(prev => ({ ...prev, [k]: true }));
    if (form) {
      const err = validateField(k, form[k]);
      setErrors(prev => ({ ...prev, [k]: err }));
    }
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { showToast("Please choose an image file (PNG, JPG, WebP)"); return; }
    if (file.size > 4 * 1024 * 1024)     { showToast("Image must be under 4 MB");    return; }
    const reader = new FileReader();
    reader.onload = () => { setAvatar(reader.result); setRemovePhoto(false); };   // data:image/...;base64,…
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form) return;

    // Validate all fields
    const newErrors = validateAll(form);
    setErrors(newErrors);
    setTouched({ fullName: true, email: true, userName: true, phone: true, bio: true });

    if (Object.keys(newErrors).length > 0) {
      const firstErrMsg = Object.values(newErrors)[0];
      showToast(`⚠️ ${firstErrMsg}`);
      return;
    }

    setSaving(true);
    try {
      // Send the data: URL only when a new file was picked; otherwise echo the
      // existing URL back so the backend leaves it alone.
      const { data: saved } = await adminApi.updateMyProfile({
        fullName: form.fullName.trim(),
        email:    form.email.trim(),
        userName: form.userName?.trim() || null,
        phone:    form.phone?.trim()    || null,
        bio:      form.bio ? form.bio.trim() : "",
        profileImageUrl: removePhoto ? "" : (avatar || form.profileImageUrl || null),
      });

      setForm(f => ({ ...f, profileImageUrl: saved.profileImageUrl || "" }));
      setAvatar(null);
      setRemovePhoto(false);
      setErrors({});
      setTouched({});

      // Keep the topbar/navbar copy of the user in step with what was saved.
      try {
        const cached = JSON.parse(localStorage.getItem("divyayoga_user") || "null");
        if (cached) {
          localStorage.setItem("divyayoga_user", JSON.stringify({
            ...cached,
            fullName: saved.fullName,
            email: saved.email,
            profileImageUrl: saved.profileImageUrl,
          }));
        }
      } catch { /* cache refresh is best-effort */ }

      // Update the sidebar / mobile header avatar right away.
      window.dispatchEvent(new CustomEvent("admin-profile-updated", {
        detail: { fullName: saved.fullName, email: saved.email, profileImageUrl: saved.profileImageUrl || "" },
      }));

      showToast("✓ Profile updated successfully");
    } catch (e) {
      showToast(e.message || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <SectionHeader title="Profile" desc="Update your personal information and public profile details."/>
        <div className="p-4 rounded-[20px]"
          style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
          <BrandLoader message="Loading profile..." minHeight="min-h-[180px]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <SectionHeader title="Profile" desc="Update your personal information and public profile details."/>
        <div className="p-5 rounded-[20px] flex items-center justify-between"
          style={{ background:"rgba(226,75,74,0.06)", border:`1px solid rgba(226,75,74,0.2)` }}>
          <span className="text-[13px]" style={{ color:T.red }}>{error}</span>
          <button onClick={load}
            className="h-9 px-4 rounded-full text-[12px] font-semibold cursor-pointer border-none text-white"
            style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})` }}>Retry</button>
        </div>
      </div>
    );
  }

  const shownAvatar = removePhoto ? null : (avatar || form.profileImageUrl);

  return (
    <div>
      <SectionHeader title="Profile" desc="Update your personal information and public profile details."/>

      {/* Avatar */}
      <div className="flex items-center gap-5 mb-7 p-5 rounded-[20px]"
        style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
        <div className="relative">
          <div className="w-[72px] h-[72px] rounded-[22px] flex items-center justify-center text-white text-[22px] font-black overflow-hidden"
            style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`,
              boxShadow:`0 8px 24px rgba(107,138,90,0.38)`, fontFamily:"'Poppins',sans-serif", position:"relative" }}>
            {shownAvatar ? (
              <img src={shownAvatar} alt="Profile" className="absolute inset-0 w-full h-full object-cover"/>
            ) : (
              <>
                <div className="absolute inset-0" style={{ background:"linear-gradient(135deg,rgba(255,255,255,0.22),transparent)" }}/>
                <span className="relative z-10">{initialsOf(form.fullName)}</span>
              </>
            )}
          </div>
          <motion.button whileHover={{ scale:1.1 }} onClick={() => fileRef.current?.click()}
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center border-2 border-white cursor-pointer"
            style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`,
              boxShadow:`0 3px 10px rgba(242,154,12,0.4)` }}>
            <FiCamera size={13} style={{ color:"#fff" }}/>
          </motion.button>
          <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} style={{ display:"none" }}/>
        </div>
        <div>
          <p className="font-black text-[15px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
            {form.fullName || "—"}
          </p>
          <p className="text-[12px] mt-0.5" style={{ color:T.muted }}>
            {form.role}{form.email ? ` · ${form.email}` : ""}
          </p>
          <div className="flex items-center gap-2 mt-1.5">
            <motion.button whileHover={{ color:T.olive }} onClick={() => fileRef.current?.click()}
              className="text-[12px] font-semibold border-none bg-transparent cursor-pointer flex items-center gap-1"
              style={{ color:T.orange, fontFamily:"'Inter',sans-serif" }}>
              <FiCamera size={12}/> {shownAvatar ? "Change Photo" : "Upload Photo"}
            </motion.button>
            {shownAvatar && (
              <>
                <span style={{ color:T.border }}>|</span>
                <motion.button whileHover={{ color:T.red }} onClick={() => { setRemovePhoto(true); setAvatar(null); }}
                  className="text-[12px] font-semibold border-none bg-transparent cursor-pointer"
                  style={{ color:T.muted, fontFamily:"'Inter',sans-serif" }}>
                  Remove
                </motion.button>
              </>
            )}
          </div>
          {avatar && (
            <p className="text-[11px] mt-1" style={{ color:T.olive }}>
              New photo selected — save to upload
            </p>
          )}
          {removePhoto && (
            <p className="text-[11px] mt-1" style={{ color:T.red }}>
              Photo will be removed — save to confirm
            </p>
          )}
        </div>
      </div>

      <div className="ad-1col grid grid-cols-2 gap-4 mb-4">
        <InputField
          label="Full Name"
          required
          value={form.fullName}
          onChange={set("fullName")}
          onBlur={blur("fullName")}
          error={errors.fullName}
          hasError={touched.fullName && !!errors.fullName}
          placeholder="Super Admin"
        />
        <InputField
          label="Username"
          value={form.userName}
          onChange={set("userName")}
          onBlur={blur("userName")}
          error={errors.userName}
          hasError={touched.userName && !!errors.userName}
          placeholder="superadmin_26"
          hint="Letters, numbers, underscores, and hyphens (3–30 chars)."
        />
        <InputField
          label="Email Address"
          required
          type="email"
          value={form.email}
          onChange={set("email")}
          onBlur={blur("email")}
          error={errors.email}
          hasError={touched.email && !!errors.email}
          placeholder="you@divyyogafoundation.com"
        />
        <InputField
          label="Phone Number"
          type="tel"
          value={form.phone}
          onChange={set("phone")}
          onBlur={blur("phone")}
          error={errors.phone}
          hasError={touched.phone && !!errors.phone}
          placeholder="+91 98765 43210"
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] font-black uppercase tracking-[0.9px] block"
            style={{ color: touched.bio && errors.bio ? T.red : T.muted }}>
            Bio
          </label>
          <span className="text-[10px] font-semibold" style={{ color: (form.bio?.length || 0) > 500 ? T.red : T.muted }}>
            {form.bio?.length || 0}/500
          </span>
        </div>
        <textarea rows={3} value={form.bio}
          onChange={e => set("bio")(e.target.value)}
          onBlur={blur("bio")}
          placeholder="A short description that appears on your profile."
          className="w-full rounded-[14px] px-4 py-2.5 text-[13px] outline-none resize-none"
          style={{
            border: `1.5px solid ${touched.bio && errors.bio ? T.red : T.border}`,
            background: touched.bio && errors.bio ? "rgba(226,75,74,0.04)" : "rgba(255,255,255,0.9)",
            color: T.dark,
            fontFamily: "'Inter',sans-serif",
            lineHeight: 1.6,
            boxShadow: touched.bio && errors.bio ? "0 0 0 3px rgba(226,75,74,0.12)" : "none",
            transition: "all 0.2s"
          }}
          onFocus={e => {
            if (!touched.bio || !errors.bio) {
              e.target.style.borderColor = T.olive;
              e.target.style.boxShadow = `0 0 0 4px rgba(107,138,90,0.1)`;
            }
          }}
        />
        {touched.bio && errors.bio && (
          <p className="text-[11px] font-bold flex items-center gap-1 mt-1.5" style={{ color: T.red }}>
            <FiAlertTriangle size={12} className="shrink-0" /> {errors.bio}
          </p>
        )}
      </div>
      <SaveBar onSave={save} saving={saving} onDiscard={load}/>
    </div>
  );
};

// ─── SECTION: SECURITY ────────────────────────────────────────────────────────
const scorePassword = (pw) => {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 6)  s++;
  if (pw.length >= 10) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 5);
};
const STRENGTH = [
  { label:"—",      pct:"0%",   color:"#CCC" },
  { label:"Weak",   pct:"20%",  color:"#E24B4A" },
  { label:"Fair",   pct:"40%",  color:"#F59E0B" },
  { label:"Good",   pct:"60%",  color:"#F29A0C" },
  { label:"Strong", pct:"80%",  color:"#6B8A5A" },
  { label:"Very strong", pct:"100%", color:"#16A34A" },
];

const relativeTime = (iso) => {
  if (!iso) return "unknown";
  const then = new Date(iso.endsWith("Z") ? iso : iso + "Z").getTime();
  const mins = Math.floor((Date.now() - then) / 60000);
  if (Number.isNaN(mins)) return "unknown";
  if (mins < 2)    return "Active now";
  if (mins < 60)   return `${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return hrs === 1 ? "1 hour ago" : `${hrs} hours ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "Yesterday" : `${days} days ago`;
};

const SecuritySection = ({ showToast }) => {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pw,          setPw]          = useState({ current:"", next:"", confirm:"" });
  const [errors,      setErrors]      = useState({});
  const [touched,     setTouched]     = useState({});
  const [saving,      setSaving]      = useState(false);

  const [sessions,        setSessions]        = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError,   setSessionsError]   = useState(null);
  const [revoking,        setRevoking]        = useState(null);

  const validatePwField = (field, value, allValues = pw) => {
    let err = null;
    const v = value || "";

    if (field === "current") {
      if (!v) err = "Current password is required";
    }

    if (field === "next") {
      if (!v) err = "New password is required";
      else if (v.length < 6) err = "New password must be at least 6 characters";
      else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(v)) err = "Password must contain uppercase, lowercase, and numbers";
      else if (allValues.current && v === allValues.current) err = "New password must differ from your current password";
    }

    if (field === "confirm") {
      if (!v) err = "Please confirm your new password";
      else if (v !== allValues.next) err = "Passwords do not match";
    }

    return err;
  };

  const validateAllPw = (values) => {
    const errs = {};
    const cErr = validatePwField("current", values.current, values);
    if (cErr) errs.current = cErr;

    const nErr = validatePwField("next", values.next, values);
    if (nErr) errs.next = nErr;

    const cfErr = validatePwField("confirm", values.confirm, values);
    if (cfErr) errs.confirm = cfErr;

    return errs;
  };

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true); setSessionsError(null);
    try {
      const { data } = await adminApi.getMySessions();
      setSessions(data || []);
    } catch (e) {
      setSessionsError(e.message || "Could not load sessions.");
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const strength = STRENGTH[scorePassword(pw.next)];

  const handleChange = (key, val) => {
    const updated = { ...pw, [key]: val };
    setPw(updated);
    if (touched[key]) {
      const err = validatePwField(key, val, updated);
      setErrors(prev => ({ ...prev, [key]: err }));
    }
    // Re-validate confirm if next changes
    if (key === "next" && touched.confirm) {
      const cfErr = validatePwField("confirm", updated.confirm, updated);
      setErrors(prev => ({ ...prev, confirm: cfErr }));
    }
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    const err = validatePwField(key, pw[key], pw);
    setErrors(prev => ({ ...prev, [key]: err }));
  };

  const save = async () => {
    const errs = validateAllPw(pw);
    setErrors(errs);
    setTouched({ current: true, next: true, confirm: true });

    if (Object.keys(errs).length > 0) {
      const firstErr = Object.values(errs)[0];
      showToast(`⚠️ ${firstErr}`);
      return;
    }

    setSaving(true);
    try {
      const { message } = await adminApi.changeMyPassword({
        currentPassword: pw.current,
        newPassword: pw.next,
        confirmNewPassword: pw.confirm,
      });
      setPw({ current:"", next:"", confirm:"" });
      setErrors({});
      setTouched({});
      showToast(`✓ ${message || "Password updated successfully"}`);
      loadSessions();   // other devices were just signed out
    } catch (e) {
      showToast(e.message || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  const revokeOne = async (id) => {
    setRevoking(id);
    try {
      await adminApi.revokeMySession(id);
      setSessions(list => list.filter(s => s.id !== id));
      showToast("Session revoked");
    } catch (e) {
      showToast(e.message || "Could not revoke session");
    } finally {
      setRevoking(null);
    }
  };

  const revokeOthers = async () => {
    try {
      await adminApi.revokeMyOtherSessions();
      setSessions(list => list.filter(s => s.isCurrent));
      showToast("All other sessions signed out");
    } catch (e) {
      showToast(e.message || "Could not revoke sessions");
    }
  };

  const others = sessions.filter(s => !s.isCurrent).length;

  const fields = [
    { key:"current", label:"Current Password",     show:showCurrent, setShow:setShowCurrent },
    { key:"next",    label:"New Password",         show:showNew,     setShow:setShowNew     },
    { key:"confirm", label:"Confirm New Password", show:showConfirm, setShow:setShowConfirm },
  ];

  return (
    <div>
      <SectionHeader title="Security" desc="Manage your password and the devices signed in to your account."/>

      {/* Change password */}
      <div className="rounded-[20px] p-5 mb-5"
        style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
        <p className="font-bold text-[14px] text-[#1F1F1F] mb-4" style={{ fontFamily:"'Poppins',sans-serif" }}>Change Password</p>
        <div className="flex flex-col gap-3.5">
          {fields.map(f => {
            const isErr = touched[f.key] && !!errors[f.key];
            return (
              <div key={f.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-black uppercase tracking-[0.9px] block"
                    style={{ color: isErr ? T.red : T.muted }}>
                    {f.label} <span style={{ color: T.red }}>*</span>
                  </label>
                </div>
                <div className="relative flex items-center rounded-[14px] overflow-hidden"
                  style={{
                    border: `1.5px solid ${isErr ? T.red : T.border}`,
                    background: isErr ? "rgba(226,75,74,0.04)" : "rgba(255,255,255,0.9)",
                    boxShadow: isErr ? "0 0 0 3px rgba(226,75,74,0.12)" : "none",
                    transition: "all 0.2s"
                  }}>
                  <input type={f.show ? "text" : "password"} placeholder="••••••••••••"
                    value={pw[f.key]}
                    onChange={e => handleChange(f.key, e.target.value)}
                    onBlur={() => handleBlur(f.key)}
                    autoComplete={f.key === "current" ? "current-password" : "new-password"}
                    className="flex-1 h-11 px-4 bg-transparent border-none outline-none text-[13px]"
                    style={{ color:T.dark, fontFamily:"'Inter',sans-serif" }}/>
                  <motion.button type="button" whileTap={{ scale:0.88 }} onClick={() => f.setShow(!f.show)}
                    className="mr-3 bg-transparent border-none cursor-pointer flex items-center"
                    style={{ color:T.light, fontSize:16, transition:"color 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.color=T.olive}
                    onMouseLeave={e => e.currentTarget.style.color=T.light}>
                    {f.show ? <FiEyeOff size={16}/> : <FiEye size={16}/>}
                  </motion.button>
                </div>
                {isErr && (
                  <p className="text-[11px] font-bold flex items-center gap-1 mt-1.5" style={{ color: T.red }}>
                    <FiAlertTriangle size={12} className="shrink-0" /> {errors[f.key]}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Password strength — driven by the actual input */}
        <div className="mt-3">
          <div className="flex justify-between text-[11px] mb-1">
            <span style={{ color:T.muted }}>Password strength</span>
            <span className="font-bold" style={{ color:strength.color }}>{strength.label}</span>
          </div>
          <div className="h-[4px] rounded-full overflow-hidden" style={{ background:"rgba(239,229,216,0.6)" }}>
            <motion.div className="h-full rounded-full"
              animate={{ width: strength.pct }} transition={{ duration:0.35, ease:"easeOut" }}
              style={{ background:strength.color }}/>
          </div>
          <p className="text-[11px] mt-2" style={{ color:T.muted }}>
            Changing your password signs out every other device.
          </p>
        </div>
        <SaveBar onSave={save} saving={saving} saveLabel="Update Password"
          onDiscard={() => setPw({ current:"", next:"", confirm:"" })}/>
      </div>

      {/* Active sessions */}
      <div className="rounded-[20px] p-5"
        style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
        <div className="flex items-center justify-between mb-4">
          <p className="font-bold text-[14px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Active Sessions</p>
          {others > 0 && (
            <motion.button whileHover={{ color:T.red }} onClick={revokeOthers}
              className="text-[12px] font-semibold border-none bg-transparent cursor-pointer"
              style={{ color:T.muted, fontFamily:"'Inter',sans-serif" }}>
              Revoke All Others ({others})
            </motion.button>
          )}
        </div>

        {sessionsLoading ? (
          <div className="flex items-center gap-3 py-2">
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24" style={{ color:T.olive }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <span className="text-[13px]" style={{ color:T.muted }}>Loading sessions…</span>
          </div>
        ) : sessionsError ? (
          <div className="flex items-center justify-between">
            <span className="text-[13px]" style={{ color:T.red }}>{sessionsError}</span>
            <button onClick={loadSessions}
              className="h-9 px-4 rounded-full text-[12px] font-semibold cursor-pointer border-none text-white"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})` }}>Retry</button>
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-[13px] py-2" style={{ color:T.muted }}>No active sessions found.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {sessions.map(s => {
              const device  = [s.browser, s.deviceName || s.operatingSystem].filter(Boolean).join(" · ") || "Unknown device";
              const place   = [s.city, s.country].filter(Boolean).join(", ") || s.ipAddress || "Unknown location";
              return (
                <div key={s.id} className="flex items-center gap-3 p-3 rounded-[14px]"
                  style={{ background:s.isCurrent ? "rgba(107,138,90,0.06)" : "rgba(239,229,216,0.2)",
                    border:`1px solid ${s.isCurrent ? "rgba(107,138,90,0.2)" : "rgba(239,229,216,0.5)"}` }}>
                  <div className="w-9 h-9 rounded-[11px] flex items-center justify-center flex-shrink-0"
                    style={{ background:s.isCurrent ? `linear-gradient(135deg,${T.olive},${T.olive2})` : "rgba(239,229,216,0.6)" }}>
                    <FiMonitor size={15} style={{ color:s.isCurrent ? "#fff" : T.muted }}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-[#1F1F1F] truncate">{device}</p>
                    <p className="text-[11px]" style={{ color:T.muted }}>
                      {place} · {s.isCurrent ? "Active now" : relativeTime(s.lastActiveAt)}
                    </p>
                  </div>
                  {s.isCurrent ? (
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full"
                      style={{ background:"rgba(34,197,94,0.1)", color:T.greenDk }}>Current</span>
                  ) : (
                    <motion.button whileHover={{ color:T.red }} whileTap={{ scale:0.9 }}
                      disabled={revoking === s.id}
                      onClick={() => revokeOne(s.id)}
                      className="w-7 h-7 rounded-[9px] flex items-center justify-center border cursor-pointer"
                      style={{ background:"rgba(255,255,255,0.9)", borderColor:"rgba(226,75,74,0.2)", color:T.red,
                        opacity: revoking === s.id ? 0.5 : 1 }}>
                      <FiX size={12}/>
                    </motion.button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};


// ─── SECTION: TEAM ACCESS (SuperAdmin only) ───────────────────────────────────
const EMPTY_STAFF_FORM = {
  fullName:"", email:"", userName:"", password:"", confirm:"", role:"SuperAdmin",
};

const roleChipColors = (role) => {
  const r = String(role || "").toLowerCase();
  if (r === "superadmin") return { bg:"rgba(242,154,12,0.12)", fg:"#C8660A" };
  if (r === "admin")      return { bg:"rgba(24,95,165,0.10)",  fg:T.blue    };
  return { bg:"rgba(107,138,90,0.10)", fg:T.olive };
};

const TeamAccessSection = ({ showToast, currentStaffId }) => {
  const [staff,    setStaff]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [form,     setForm]     = useState(EMPTY_STAFF_FORM);
  const [errors,   setErrors]   = useState({});
  const [touched,  setTouched]  = useState({});
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState(null);

  const validateStaffField = (field, value, allValues = form) => {
    let err = null;
    const v = (value || "").toString().trim();

    if (field === "fullName") {
      if (!v) err = "Full name is required";
      else if (v.length < 2) err = "Full name must be at least 2 characters";
      else if (v.length > 100) err = "Full name cannot exceed 100 characters";
      else if (!/^[a-zA-Z\s.'\-]+$/.test(v)) err = "Full name can only contain letters, spaces, dots, and hyphens";
      else if (/<script|javascript:/i.test(v)) err = "Invalid characters detected";
    }

    if (field === "email") {
      if (!v) err = "Email address is required";
      else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v)) err = "Enter a valid email address";
      else if (v.length > 150) err = "Email cannot exceed 150 characters";
    }

    if (field === "userName" && v) {
      if (v.length < 3 || v.length > 30) err = "Username must be between 3 and 30 characters";
      else if (!/^[a-zA-Z0-9_.-]+$/.test(v)) err = "Username can only contain letters, numbers, underscores, and hyphens";
      else if (/<script|javascript:/i.test(v)) err = "Invalid characters detected";
    }

    if (field === "role") {
      if (!v || !["SuperAdmin", "Admin", "Trainer"].includes(v)) err = "Please select a valid staff role";
    }

    if (field === "password") {
      const rawPw = value || "";
      if (!rawPw) err = "Password is required";
      else if (rawPw.length < 6) err = "Password must be at least 6 characters";
      else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(rawPw)) err = "Must contain uppercase, lowercase, and numbers";
    }

    if (field === "confirm") {
      const rawCf = value || "";
      if (!rawCf) err = "Please confirm the password";
      else if (rawCf !== allValues.password) err = "Passwords do not match";
    }

    return err;
  };

  const validateAllStaff = (data) => {
    const errs = {};
    const fnErr = validateStaffField("fullName", data.fullName, data);
    if (fnErr) errs.fullName = fnErr;

    const emErr = validateStaffField("email", data.email, data);
    if (emErr) errs.email = emErr;

    const unErr = validateStaffField("userName", data.userName, data);
    if (unErr) errs.userName = unErr;

    const rlErr = validateStaffField("role", data.role, data);
    if (rlErr) errs.role = rlErr;

    const pwErr = validateStaffField("password", data.password, data);
    if (pwErr) errs.password = pwErr;

    const cfErr = validateStaffField("confirm", data.confirm, data);
    if (cfErr) errs.confirm = cfErr;

    return errs;
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await adminApi.getStaff();
      setStaff(data || []);
    } catch (e) {
      setError(e.message || "Could not load staff accounts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => {
    const updated = { ...form, [k]: v };
    setForm(updated);
    if (touched[k]) {
      const err = validateStaffField(k, v, updated);
      setErrors(prev => ({ ...prev, [k]: err }));
    }
    if (k === "password" && touched.confirm) {
      const cfErr = validateStaffField("confirm", updated.confirm, updated);
      setErrors(prev => ({ ...prev, confirm: cfErr }));
    }
  };

  const blur = (k) => () => {
    setTouched(prev => ({ ...prev, [k]: true }));
    const err = validateStaffField(k, form[k], form);
    setErrors(prev => ({ ...prev, [k]: err }));
  };

  const create = async () => {
    const errs = validateAllStaff(form);
    setErrors(errs);
    setTouched({ fullName: true, email: true, userName: true, role: true, password: true, confirm: true });

    if (Object.keys(errs).length > 0) {
      const firstErr = Object.values(errs)[0];
      showToast(`⚠️ ${firstErr}`);
      return;
    }

    const fullName = form.fullName.trim();
    const email    = form.email.trim();
    const userName = form.userName.trim();

    setCreating(true);
    try {
      const { message } = await adminApi.createStaff({
        fullName,
        email,
        userName: userName || null,
        password: form.password,
        role: form.role,
      });
      setForm(EMPTY_STAFF_FORM);
      setErrors({});
      setTouched({});
      await load();
      showToast(`✓ ${message || "Staff account created successfully"}`);
    } catch (e) {
      showToast(e.message || "Could not create the account");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (member) => {
    setToggling(member.id);
    try {
      const { message } = await adminApi.toggleStaffStatus(member.id);
      setStaff(list => list.map(s => s.id === member.id ? { ...s, isActive: !s.isActive } : s));
      showToast(message || "Status updated");
    } catch (e) {
      showToast(e.message || "Could not update the account");
    } finally {
      setToggling(null);
    }
  };

  const superAdmins = staff.filter(s => String(s.role).toLowerCase() === "superadmin" && s.isActive).length;
  const selectedRole = STAFF_ROLES.find(r => r.value === form.role);

  return (
    <div>
      <SectionHeader title="Team Access"
        desc="Create staff accounts and control who can sign in to the admin dashboard."/>

      {/* Add a new account */}
      <div className="rounded-[20px] p-5 mb-5"
        style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0"
            style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`,
              boxShadow:`0 5px 14px rgba(242,154,12,0.32)` }}>
            <FiUserPlus size={16} style={{ color:"#fff" }}/>
          </div>
          <div>
            <p className="font-bold text-[14px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
              Add Team Member
            </p>
            <p className="text-[11px]" style={{ color:T.muted }}>
              They sign in with this email or username and the password you set here.
            </p>
          </div>
        </div>

        <div className="ad-1col grid grid-cols-2 gap-4 mb-4">
          <InputField
            label="Full Name"
            required
            value={form.fullName}
            onChange={set("fullName")}
            onBlur={blur("fullName")}
            error={errors.fullName}
            hasError={touched.fullName && !!errors.fullName}
            placeholder="Asha Menon"
          />
          <InputField
            label="Email Address"
            required
            type="email"
            value={form.email}
            onChange={set("email")}
            onBlur={blur("email")}
            error={errors.email}
            hasError={touched.email && !!errors.email}
            placeholder="asha@divyyogafoundation.com"
          />
          <InputField
            label="Username"
            value={form.userName}
            onChange={set("userName")}
            onBlur={blur("userName")}
            error={errors.userName}
            hasError={touched.userName && !!errors.userName}
            placeholder="asha_admin"
            hint="Optional — 3–30 alphanumeric chars."
          />
          <div>
            <CustomSelect
              label="Role"
              required
              value={form.role}
              onChange={val => {
                set("role")(val);
              }}
              onBlur={blur("role")}
              hasError={touched.role && !!errors.role}
              options={STAFF_ROLES}
            />
            {touched.role && errors.role ? (
              <p className="text-[11px] font-bold flex items-center gap-1 mt-1.5" style={{ color: T.red }}>
                <FiAlertTriangle size={12} className="shrink-0" /> {errors.role}
              </p>
            ) : (
              <p className="text-[11px] mt-1.5" style={{ color: T.muted }}>{selectedRole?.desc}</p>
            )}
          </div>
          <InputField
            label="Password"
            required
            type="password"
            value={form.password}
            onChange={set("password")}
            onBlur={blur("password")}
            error={errors.password}
            hasError={touched.password && !!errors.password}
            placeholder="••••••••••••"
            hint="At least 6 chars with uppercase, lowercase, and numbers."
          />
          <InputField
            label="Confirm Password"
            required
            type="password"
            value={form.confirm}
            onChange={set("confirm")}
            onBlur={blur("confirm")}
            error={errors.confirm}
            hasError={touched.confirm && !!errors.confirm}
            placeholder="••••••••••••"
          />
        </div>

        {form.role === "SuperAdmin" && (
          <div className="flex items-start gap-2.5 rounded-[14px] px-4 py-3"
            style={{ background:"rgba(242,154,12,0.07)", border:"1px solid rgba(242,154,12,0.2)" }}>
            <FiShield size={14} style={{ color:T.orange, marginTop:2, flexShrink:0 }}/>
            <p className="text-[11.5px] leading-[1.6]" style={{ color:"#8A5A12" }}>
              A Super Admin has full access — including creating and deactivating other
              staff accounts, this one included.
            </p>
          </div>
        )}

        <SaveBar onSave={create} saving={creating} saveLabel="Create Account"
          onDiscard={() => { setForm(EMPTY_STAFF_FORM); setErrors({}); setTouched({}); }}/>
      </div>

      {/* Existing accounts */}
      <div className="rounded-[20px] p-5"
        style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
        <div className="flex items-center justify-between mb-4">
          <p className="font-bold text-[14px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
            Staff Accounts
          </p>
          <span className="text-[11px]" style={{ color:T.muted }}>
            {staff.length} account{staff.length === 1 ? "" : "s"} · {superAdmins} active super admin{superAdmins === 1 ? "" : "s"}
          </span>
        </div>

        {loading ? (
          <BrandLoader message="Loading staff accounts..." minHeight="min-h-[160px]" />
        ) : error ? (
          <div className="flex items-center justify-between">
            <span className="text-[13px]" style={{ color:T.red }}>{error}</span>
            <button onClick={load}
              className="h-9 px-4 rounded-full text-[12px] font-semibold cursor-pointer border-none text-white"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})` }}>Retry</button>
          </div>
        ) : staff.length === 0 ? (
          <p className="text-[13px] py-2" style={{ color:T.muted }}>No staff accounts yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {staff.map(member => {
              const chip = roleChipColors(member.role);
              const isMe = currentStaffId != null && member.id === currentStaffId;
              return (
                <div key={member.id} className="flex items-center gap-3 p-3 rounded-[14px]"
                  style={{ background: member.isActive ? "rgba(239,229,216,0.2)" : "rgba(226,75,74,0.04)",
                    border:`1px solid ${member.isActive ? "rgba(239,229,216,0.5)" : "rgba(226,75,74,0.15)"}` }}>
                  <div className="w-9 h-9 rounded-[11px] flex items-center justify-center flex-shrink-0 text-white text-[11px] font-black"
                    style={{ background: member.isActive
                      ? `linear-gradient(135deg,${T.olive},${T.olive2})`
                      : "rgba(239,229,216,0.9)",
                      color: member.isActive ? "#fff" : T.muted,
                      fontFamily:"'Poppins',sans-serif" }}>
                    {initialsOf(member.fullName)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[13px] font-semibold text-[#1F1F1F] truncate">{member.fullName}</p>
                      {isMe && (
                        <span className="text-[9px] font-black uppercase tracking-[0.6px] px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{ background:"rgba(107,138,90,0.12)", color:T.olive }}>You</span>
                      )}
                    </div>
                    <p className="text-[11px] truncate" style={{ color:T.muted }}>
                      {member.email}{member.userName ? ` · @${member.userName}` : ""}
                    </p>
                  </div>

                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0"
                    style={{ background:chip.bg, color:chip.fg }}>
                    {member.role}
                  </span>

                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full flex-shrink-0"
                    style={{ background: member.isActive ? "rgba(34,197,94,0.1)" : "rgba(226,75,74,0.1)",
                      color: member.isActive ? T.greenDk : T.red }}>
                    {member.isActive ? "Active" : "Disabled"}
                  </span>

                  <motion.button whileHover={{ y:-1 }} whileTap={{ scale:0.97 }}
                    disabled={toggling === member.id}
                    onClick={() => toggle(member)}
                    className="h-8 px-3.5 rounded-full text-[11px] font-bold cursor-pointer flex-shrink-0"
                    style={{ background:"rgba(255,255,255,0.9)",
                      border:`1.5px solid ${member.isActive ? "rgba(226,75,74,0.25)" : "rgba(107,138,90,0.3)"}`,
                      color: member.isActive ? T.red : T.olive,
                      fontFamily:"'Inter',sans-serif",
                      opacity: toggling === member.id ? 0.5 : 1 }}>
                    {member.isActive ? "Deactivate" : "Activate"}
                  </motion.button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── SECTION: PLATFORM CONFIG ─────────────────────────────────────────────────
const PlatformSection = ({ showToast }) => {
  const [maintenance, setMaintenance] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = () => { setSaving(true); setTimeout(() => { setSaving(false); showToast("✓ Platform settings saved"); }, 1400); };

  return (
    <div>
      <SectionHeader title="Platform Config" desc="Configure global platform settings, timezone, and operational modes."/>
      <div className="flex flex-col gap-4">
        <div className="rounded-[20px] p-5" style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
          <p className="font-bold text-[13px] text-[#1F1F1F] mb-4" style={{ fontFamily:"'Poppins',sans-serif" }}>General</p>
          <div className="ad-1col grid grid-cols-2 gap-4">
            <InputField label="Platform Name"  defaultValue="Divyyog Foundation"/>
            <InputField label="Tagline"         defaultValue="Transform Mind, Body & Soul"/>
            <InputField label="Support Email"  defaultValue="support@divyyog.com" type="email"/>
            <InputField label="Support Phone"  defaultValue="+91 98765 43210"/>
          </div>
          <div className="ad-1col grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color:T.muted }}>Timezone</label>
              <select className="w-full rounded-[14px] px-4 py-2.5 text-[13px] outline-none cursor-pointer"
                style={{ border:`1.5px solid ${T.border}`, background:"rgba(255,255,255,0.9)", color:T.dark, fontFamily:"'Inter',sans-serif" }}>
                {TIMEZONES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color:T.muted }}>Currency</label>
              <select className="w-full rounded-[14px] px-4 py-2.5 text-[13px] outline-none cursor-pointer"
                style={{ border:`1.5px solid ${T.border}`, background:"rgba(255,255,255,0.9)", color:T.dark, fontFamily:"'Inter',sans-serif" }}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="rounded-[20px] p-5" style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-[13px] flex items-center justify-center"
                style={{ background:maintenance ? "rgba(226,75,74,0.1)" : "rgba(239,229,216,0.5)" }}>
                <FiSettings size={18} style={{ color:maintenance ? T.red : T.muted }}/>
              </div>
              <div>
                <p className="font-bold text-[14px] text-[#1F1F1F]">Maintenance Mode</p>
                <p className="text-[12px]" style={{ color:maintenance ? T.red : T.muted }}>
                  {maintenance ? "⚠️ Platform is in maintenance mode: users cannot access" : "Platform is live and accessible"}
                </p>
              </div>
            </div>
            <Toggle on={maintenance} onChange={(v) => { setMaintenance(v); showToast(v ? "⚠️ Maintenance mode ON" : "✓ Platform is live"); }}/>
          </div>
        </div>
      </div>
      <SaveBar onSave={save} saving={saving}/>
    </div>
  );
};

// ─── SECTION: NOTIFICATION PREFS ─────────────────────────────────────────────
const NotifPrefsSection = ({ showToast }) => {
  const [prefs, setPrefs] = useState(
    Object.fromEntries(NOTIF_PREFS.map(p => [p.id, p.default]))
  );
  const [saving, setSaving] = useState(false);
  const save = () => { setSaving(true); setTimeout(() => { setSaving(false); showToast("✓ Notification preferences saved"); }, 1400); };

  return (
    <div>
      <SectionHeader title="Notification Preferences" desc="Choose which events trigger admin alerts and notifications."/>
      <div className="rounded-[20px] overflow-hidden"
        style={{ background:"rgba(255,255,255,0.7)", border:`1px solid ${T.border}` }}>
        {NOTIF_PREFS.map((pref, i) => (
          <div key={pref.id}
            className="flex items-center justify-between px-5 py-4"
            style={{ borderBottom: i < NOTIF_PREFS.length-1 ? `1px solid rgba(239,229,216,0.5)` : "none" }}>
            <div>
              <p className="text-[13px] font-semibold text-[#1F1F1F]">{pref.label}</p>
              <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>{pref.desc}</p>
            </div>
            <Toggle on={prefs[pref.id]}
              onChange={(v) => setPrefs(prev => ({ ...prev, [pref.id]:v }))}/>
          </div>
        ))}
      </div>
      <SaveBar onSave={save} saving={saving}/>
    </div>
  );
};

// ─── SECTION: APPEARANCE ──────────────────────────────────────────────────────
// ─── SECTION: APPEARANCE (With Real Data Binding & Image Uploads) ─────────────
const AppearanceSection = ({ showToast }) => {
  const [primaryColor, setPrimaryColor] = useState("#F29A0C");
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [bgMusicUrl, setBgMusicUrl] = useState(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const logoFileRef = useRef(null);
  const faviconFileRef = useRef(null);
  const audioFileRef = useRef(null);

  const PRESET_COLORS = ["#F29A0C", "#6B8A5A", "#534AB7", "#185FA5", "#E24B4A", "#0F6E56"];

  const loadAppearance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAppearanceSettings();
      if (res.data) {
        setPrimaryColor(res.data.primaryColor || res.data.PrimaryColor || "#F29A0C");
        setLogoUrl(res.data.logoUrl || res.data.LogoUrl || null);
        setFaviconUrl(res.data.faviconUrl || res.data.FaviconUrl || null);
        setBgMusicUrl(res.data.bgMusicUrl || res.data.BgMusicUrl || "/assets/divyyog_music.mp3");
      }
    } catch {
      // Fallback to default
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAppearance();
  }, [loadAppearance]);

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { showToast("Please choose an image file for logo", "error"); return; }
    if (file.size > 4 * 1024 * 1024) { showToast("Logo image must be under 4MB", "error"); return; }
    const reader = new FileReader();
    reader.onload = () => { setLogoUrl(reader.result); showToast("✓ Logo selected. Click Save to apply."); };
    reader.readAsDataURL(file);
  };

  const handleFaviconPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { showToast("Please choose an image file for favicon", "error"); return; }
    if (file.size > 2 * 1024 * 1024) { showToast("Favicon image must be under 2MB", "error"); return; }
    const reader = new FileReader();
    reader.onload = () => { setFaviconUrl(reader.result); showToast("✓ Favicon selected. Click Save to apply."); };
    reader.readAsDataURL(file);
  };

  const handleAudioFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = [".mp3", ".wav", ".m4a", ".aac", ".ogg"];
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowed.includes(ext)) {
      showToast("Invalid audio file. Allowed formats: .mp3, .wav, .m4a, .aac, .ogg", "error");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showToast("Audio file size must be under 25MB", "error");
      return;
    }

    setUploadingAudio(true);
    try {
      const res = await adminApi.uploadAudio(file);
      const newAudioUrl = res.data?.audioUrl || res.data?.AudioUrl;
      if (newAudioUrl) {
        setBgMusicUrl(newAudioUrl);
        showToast("✓ Background audio uploaded directly to Cloudflare R2 audio folder!");
      }
    } catch (err) {
      showToast(err.message || "Failed to upload audio file to R2.", "error");
    } finally {
      setUploadingAudio(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { primaryColor, logoUrl, faviconUrl, bgMusicUrl };
      const res = await adminApi.updateAppearanceSettings(payload);
      if (res.data?.success || res.success) {
        showToast("✓ Appearance settings saved");
      }
    } catch (err) {
      showToast(err.message || "Failed to save appearance settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <SectionHeader title="Appearance" desc="Customise platform branding, theme colours, and logo." />
        <div className="flex items-center gap-3 p-5 rounded-[20px]" style={{ background: "rgba(255,255,255,0.7)", border: `1px solid ${T.border}` }}>
          <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24" style={{ color: T.olive }}>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-[13px]" style={{ color: T.muted }}>Loading appearance settings…</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SectionHeader title="Appearance" desc="Customise platform branding, website background music, and logo." />
      <div className="flex flex-col gap-4">

        {/* Website Background Music */}
        <div className="rounded-[20px] p-5" style={{ background: "rgba(255,255,255,0.7)", border: `1px solid ${T.border}` }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-bold text-[13px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>Website Background Music</p>
              <p className="text-[11.5px] text-zinc-500 mt-0.5">Upload audio files for website background music</p>
            </div>
            {bgMusicUrl && (
              <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                Active Audio Track
              </span>
            )}
          </div>

          <div className="flex flex-col md:flex-row items-center gap-4 mt-4 p-4 rounded-2xl bg-zinc-50/80 border border-zinc-200/80">
            <input ref={audioFileRef} type="file" accept="audio/*" onChange={handleAudioFileUpload} className="hidden" />
            
            {/* Upload Button */}
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={uploadingAudio}
              onClick={() => audioFileRef.current?.click()}
              className="px-5 py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-extrabold flex items-center gap-2 border-none cursor-pointer shadow-md disabled:opacity-50 shrink-0"
            >
              {uploadingAudio ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Uploading audio file…</span>
                </>
              ) : (
                <>
                  <FiMusic size={16} />
                  <span>Upload Audio File</span>
                </>
              )}
            </motion.button>

            {/* Audio Preview Player */}
            <div className="flex-1 w-full min-w-0">
              {bgMusicUrl ? (
                <div className="flex items-center gap-3">
                  <audio controls src={bgMusicUrl} className="w-full h-9 rounded-lg" style={{ accentColor: "#F29A0C" }} />
                  <button
                    type="button"
                    onClick={() => { setBgMusicUrl("/assets/divyyog_music.mp3"); showToast("Reset to default background music"); }}
                    className="text-[11px] font-bold text-zinc-400 hover:text-red-500 whitespace-nowrap bg-transparent border-none cursor-pointer"
                  >
                    Reset
                  </button>
                </div>
              ) : (
                <span className="text-[12px] text-zinc-400 italic">No custom audio uploaded yet. Reverting to default track.</span>
              )}
            </div>
          </div>
        </div>

        {/* Logo & Favicon upload */}
        <div className="rounded-[20px] p-5" style={{ background: "rgba(255,255,255,0.7)", border: `1px solid ${T.border}` }}>
          <p className="font-bold text-[13px] text-[#1F1F1F] mb-4" style={{ fontFamily: "'Poppins',sans-serif" }}>Logo & Branding</p>
          <div className="ad-1col grid grid-cols-2 gap-4">
            
            {/* Platform Logo */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color: T.muted }}>Platform Logo</label>
              <input ref={logoFileRef} type="file" accept="image/*" onChange={handleLogoPick} className="hidden" />
              <motion.div
                whileHover={{ borderColor: T.olive, background: "rgba(107,138,90,0.05)" }}
                onClick={() => logoFileRef.current?.click()}
                className="relative h-[90px] rounded-[14px] flex flex-col items-center justify-center gap-1.5 cursor-pointer overflow-hidden border-2 border-dashed transition-all"
                style={{ borderColor: logoUrl ? T.olive : "rgba(239,229,216,0.9)", background: "rgba(255,255,255,0.6)" }}
              >
                {logoUrl ? (
                  <div className="w-full h-full p-2 flex items-center justify-center bg-white/90">
                    <img src={logoUrl} alt="Platform Logo" className="max-h-full max-w-full object-contain" />
                  </div>
                ) : (
                  <>
                    <FiImage size={20} style={{ color: T.muted }} />
                    <span className="text-[11px] font-semibold" style={{ color: T.dark }}>Upload Platform Logo</span>
                    <span className="text-[10px]" style={{ color: T.light }}>1:1 or banner ratio, max 4MB</span>
                  </>
                )}
              </motion.div>
              {logoUrl && (
                <button
                  type="button"
                  onClick={() => setLogoUrl(null)}
                  className="text-[11px] text-red-500 font-bold mt-1 bg-transparent border-none cursor-pointer"
                >
                  Remove Logo
                </button>
              )}
            </div>

            {/* Favicon */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-[0.9px] block mb-1.5" style={{ color: T.muted }}>Favicon Icon</label>
              <input ref={faviconFileRef} type="file" accept="image/*" onChange={handleFaviconPick} className="hidden" />
              <motion.div
                whileHover={{ borderColor: T.olive, background: "rgba(107,138,90,0.05)" }}
                onClick={() => faviconFileRef.current?.click()}
                className="relative h-[90px] rounded-[14px] flex flex-col items-center justify-center gap-1.5 cursor-pointer overflow-hidden border-2 border-dashed transition-all"
                style={{ borderColor: faviconUrl ? T.olive : "rgba(239,229,216,0.9)", background: "rgba(255,255,255,0.6)" }}
              >
                {faviconUrl ? (
                  <div className="w-full h-full p-2 flex items-center justify-center bg-white/90">
                    <img src={faviconUrl} alt="Favicon" className="w-10 h-10 object-contain" />
                  </div>
                ) : (
                  <>
                    <FiImage size={20} style={{ color: T.muted }} />
                    <span className="text-[11px] font-semibold" style={{ color: T.dark }}>Upload Favicon</span>
                    <span className="text-[10px]" style={{ color: T.light }}>32×32 or 64×64px PNG/ICO</span>
                  </>
                )}
              </motion.div>
              {faviconUrl && (
                <button
                  type="button"
                  onClick={() => setFaviconUrl(null)}
                  className="text-[11px] text-red-500 font-bold mt-1 bg-transparent border-none cursor-pointer"
                >
                  Remove Favicon
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Colour theme (Commented out as requested) */}
        {/* <div className="rounded-[20px] p-5" style={{ background: "rgba(255,255,255,0.7)", border: `1px solid ${T.border}` }}>
          <p className="font-bold text-[13px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>Primary Theme Colour</p>
          <div className="flex items-center gap-4 mt-4">
            <div className="flex gap-2.5">
              {PRESET_COLORS.map(c => (
                <motion.button key={c} whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}
                  onClick={() => { setPrimaryColor(c); showToast(`Theme colour selected: ${c}`); }}
                  className="w-9 h-9 rounded-full border-2 cursor-pointer flex-shrink-0"
                  style={{
                    background: c,
                    borderColor: primaryColor === c ? T.dark : "transparent",
                    boxShadow: primaryColor === c ? `0 0 0 3px ${c}40` : "none",
                    transition: "all 0.2s"
                  }}>
                  {primaryColor === c && (
                    <div className="w-full h-full flex items-center justify-center">
                      <FiCheckCircle size={14} style={{ color: "#fff" }} />
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
            <div className="flex items-center gap-2 rounded-[12px] px-3 py-2 flex-shrink-0"
              style={{ border: `1.5px solid ${T.border}`, background: "rgba(255,255,255,0.9)" }}>
              <div className="w-5 h-5 rounded-full" style={{ background: primaryColor }} />
              <span className="text-[12px] font-mono font-bold" style={{ color: T.dark }}>{primaryColor}</span>
            </div>
          </div>
        </div> */}
      </div>
      <SaveBar onSave={save} saving={saving} onDiscard={loadAppearance} />
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function AdminSettingsPage() {
  const [activeSection, setActiveSection] = useState("profile");
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => {
    setToast(msg); setTimeout(() => setToast(null), 3200);
  }, []);

  // Seeded from the cached login role so the nav does not flicker, then confirmed
  // against the server. Only affects what is shown — every staff endpoint checks
  // the role on its own.
  const [me, setMe] = useState(() => ({
    id: null,
    role: localStorage.getItem("divyayoga_role") || "",
  }));

  useEffect(() => {
    let cancelled = false;
    adminApi.getMyProfile()
      .then(({ data }) => {
        if (!cancelled && data) setMe({ id: data.id ?? null, role: data.role || "" });
      })
      .catch(() => { /* keep the cached role; the API stays the real gate */ });
    return () => { cancelled = true; };
  }, []);

  const isSuperAdmin = String(me.role).toLowerCase() === "superadmin";
  const navItems = NAV_ITEMS.filter(item => !item.superAdminOnly || isSuperAdmin);

  const SECTION_COMPONENTS = {
    profile:    <ProfileSection    showToast={showToast}/>,
    security:   <SecuritySection   showToast={showToast}/>,
    team:       <TeamAccessSection showToast={showToast} currentStaffId={me.id}/>,
    platform:   <PlatformSection   showToast={showToast}/>,
    notifprefs: <NotifPrefsSection showToast={showToast}/>,
    appearance: <AppearanceSection showToast={showToast}/>,
  };

  // Guards the case where the confirmed role turns out not to be SuperAdmin after
  // the section was already open.
  const visibleSection = activeSection === "team" && !isSuperAdmin ? "profile" : activeSection;

  return (
    <AdminDashboardLayout activeNav="settings">
      <div style={{ fontFamily:"'Inter',sans-serif" }}>

        {/* Ambient orbs */}
        <motion.div animate={{ y:[0,-18,0], x:[0,14,0] }}
          transition={{ duration:14, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)",
            filter:"blur(80px)", top:-120, right:-60, zIndex:0 }}/>
        <motion.div animate={{ y:[0,14,0], x:[0,-10,0] }}
          transition={{ duration:17, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[300px] h-[300px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.05),transparent 70%)",
            filter:"blur(80px)", bottom:-60, left:40, zIndex:0 }}/>

        <div className="relative z-10 w-full min-w-0">

          {/* Topbar */}
          <motion.div initial={{ opacity:0, y:-16 }} animate={{ opacity:1, y:0 }}
            className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-black text-[#1F1F1F]"
                style={{ fontFamily:"'Poppins',sans-serif", fontSize:26, letterSpacing:"-1px" }}>
                Settings
              </h1>
              <p className="text-[12px] mt-1" style={{ color:T.muted }}>
                Manage your account, platform configuration, and preferences
              </p>
            </div>
          </motion.div>

          {/* Two-column layout */}
          <div className="ad-1col grid gap-5" style={{ gridTemplateColumns:"240px 1fr" }}>

            {/* LEFT   Settings nav */}
            <motion.div initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }}
              transition={{ delay:0.08, duration:0.6, ease:[0.22,1,0.36,1] }}>
              <div className="ad-setnav rounded-[24px] p-3 sticky top-6" style={glass}>
                {navItems.map((item, i) => (
                  <motion.button key={item.id}
                    initial={{ opacity:0, x:-10 }} animate={{ opacity:1, x:0 }}
                    transition={{ delay:0.1 + i*0.05 }}
                    whileHover={visibleSection !== item.id ? { x:4 } : {}}
                    onClick={() => setActiveSection(item.id)}
                    className="w-full flex items-center gap-3 px-3.5 py-3 rounded-[14px] mb-1 last:mb-0 border-none cursor-pointer text-left"
                    style={{
                      background: visibleSection===item.id
                        ? item.danger
                          ? "rgba(226,75,74,0.08)"
                          : `linear-gradient(135deg,${T.olive},${T.olive2})`
                        : "transparent",
                      boxShadow: visibleSection===item.id && !item.danger
                        ? `0 5px 16px rgba(107,138,90,0.3)` : "none",
                      transition:"all 0.22s",
                    }}>
                    <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0"
                      style={{
                        background: visibleSection===item.id
                          ? "rgba(255,255,255,0.22)"
                          : item.danger ? "rgba(226,75,74,0.1)" : "rgba(239,229,216,0.5)",
                      }}>
                      <item.icon size={15}
                        style={{ color: visibleSection===item.id ? "#fff"
                          : item.danger ? T.red : T.muted }}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-bold truncate"
                        style={{ color: visibleSection===item.id ? "#fff"
                          : item.danger ? T.red : T.dark }}>
                        {item.label}
                      </p>
                    </div>
                    {visibleSection===item.id && (
                      <FiChevronRight size={14} style={{ color:"rgba(255,255,255,0.7)", flexShrink:0 }}/>
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* RIGHT   Section content */}
            <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }}
              transition={{ delay:0.12, duration:0.6 }}>
              <div className="ad-card rounded-[24px] p-7" style={glass}>
                <AnimatePresence mode="wait">
                  <motion.div key={visibleSection}
                    initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                    exit={{ opacity:0, y:-12 }}
                    transition={{ duration:0.35, ease:[0.22,1,0.36,1] }}>
                    {SECTION_COMPONENTS[visibleSection]}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div key={toast}
              initial={{ opacity:0, y:16, scale:0.95 }} animate={{ opacity:1, y:0, scale:1 }}
              exit={{ opacity:0, y:16 }}
              className="ad-toast fixed bottom-7 right-7 z-[300] flex items-center gap-3 px-5 py-3.5 rounded-[16px] text-white text-[13px] font-semibold"
              style={{ background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)",
                boxShadow:"0 16px 48px rgba(0,0,0,0.25)", fontFamily:"'Inter',sans-serif" }}>
              <FiCheckCircle size={16} style={{ color:T.olive }}/>{toast}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminDashboardLayout>
  );
}